import { remove } from 'lodash-es';
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { PatientAttachment } from '../../../core/models/patient-attachment.model';
import { ReferralMatchVerification } from '../../../core/models/referral-match-verification.model';
import { PatientPrescriber } from '../../../core/models/patient-prescriber.model';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  isConsultNoteVerificationFrozen,
  isClientDateReferralMatchVerification,
} from '../../../core/lib/verification-utils';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { PatientPrescriberService } from '../../../core/services/patient-prescriber.service';
import { VerificationType } from '../../../core/enums/verification-type.enum';
import {
  ReferralMatchVerificationEvent,
  ReferralMatchVerificationEventType
} from 'app/core/models/referral-match-verification-event.model';
import { FormsModule } from '@angular/forms';
import { EditReferralMatchVerificationsStandardComponent } from '../edit-referral-match-verifications-standard/edit-referral-match-verifications-standard.component';
import { EditReferralMatchVerificationsClientComponent } from '../edit-referral-match-verifications-client/edit-referral-match-verifications-client.component';
import { EditReferralMatchVerificationsEhrComponent } from '../edit-referral-match-verifications-ehr/edit-referral-match-verifications-ehr.component';

import { EditReferralMatchVerificationsClientDatesComponent } from '../edit-referral-match-verifications-client-dates/edit-referral-match-verifications-client-dates.component';
import { AdditionalDetailsInputComponent } from '../../../shared/components/additional-details-input/additional-details-input.component';

@Component({
    selector: 'app-edit-referral-match-verifications-modal',
    templateUrl: './edit-referral-match-verifications-modal.component.html',
    styleUrls: ['./edit-referral-match-verifications-modal.component.scss'],
    imports: [FormsModule,
              EditReferralMatchVerificationsStandardComponent,
              EditReferralMatchVerificationsClientComponent,
              EditReferralMatchVerificationsEhrComponent,
              EditReferralMatchVerificationsClientDatesComponent,
              AdditionalDetailsInputComponent]
})
export class EditReferralMatchVerificationsModalComponent implements OnInit {
  private authService = inject(AuthenticationService);
  private patientPrescriberService = inject(PatientPrescriberService);
  private activeModal = inject(NgbActiveModal);

  @Input() patientAttachment: PatientAttachment;
  @Input() patientPrescriber: PatientPrescriber;

  @Output() patientPrescriberChange = new EventEmitter<PatientPrescriber>();

  notesFrozen = false;
  showClientReferralDatesSection = false;

  ngOnInit() {
    this.notesFrozen = isConsultNoteVerificationFrozen(
      this.authService,
      this.patientAttachment,
      VerificationType.referralMatch
    );
    this.showClientReferralDatesSection = this.shouldShowClientReferralDatesSection();
  }

  onCloseClick() {
    this.activeModal.close();
  }

  onVerificationChange(event: ReferralMatchVerificationEvent) {
    switch (event.type) {
      case ReferralMatchVerificationEventType.changed:
        this.updateReferralVerification(event.verification, event.tempId);
        break;
      case ReferralMatchVerificationEventType.removed:
        this.removeReferralVerification(event.verification);
        break;
    }
  }

  onNotesChange(value: string) {
    this.patientPrescriberService
      .update(this.patientPrescriber.id, value)
      .subscribe((patientPrescriber: PatientPrescriber) => {
        const modifiedPatientPrescriber: PatientPrescriber = Object.assign({}, this.patientPrescriber);
        modifiedPatientPrescriber.referralMatchVerificationNotes = patientPrescriber.referralMatchVerificationNotes;
        this.patientPrescriberChange.emit(modifiedPatientPrescriber);
      });
  }

  private updateReferralVerification(rmv: ReferralMatchVerification, tempId?: string) {
    const modifiedPatientPrescriber: PatientPrescriber = Object.assign({}, this.patientPrescriber);

    const index = modifiedPatientPrescriber.referralMatchVerifications.findIndex(
      ppRmv => ppRmv.id === rmv.id || ppRmv.id === tempId
    );

    if (index > -1) {
      modifiedPatientPrescriber.referralMatchVerifications[index] = rmv;
    } else {
      modifiedPatientPrescriber.referralMatchVerifications.push(rmv);
    }

    this.patientPrescriberChange.emit(modifiedPatientPrescriber);
  }

  private removeReferralVerification(rmv: ReferralMatchVerification) {
    const modifiedPatientPrescriber: PatientPrescriber = Object.assign({}, this.patientPrescriber);
    remove(modifiedPatientPrescriber.referralMatchVerifications, ppRmv => ppRmv.id === rmv.id);
    this.patientPrescriberChange.emit(modifiedPatientPrescriber);
  }

  private shouldShowClientReferralDatesSection(): boolean {
    return this.patientPrescriber.referralMatchVerifications.some(isClientDateReferralMatchVerification);
  }
}
